// Bracelet and Recommendation Components
import React, { useState } from 'react';
import { toast } from 'sonner';

// ══════════════════════════════════════════
// BRACELET STONE CONFIGURATION SECTION
// ══════════════════════════════════════════

export const BraceletConfigSection = ({ order, onSave, isAnalysisDone }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const STONES = [
    'Green Aventurine', 'Red Garnet', 'Tiger Eye', 'Clear Quartz',
    'Moon Stone', 'Citrine', 'Black Obsidian', 'Green Jade',
    'Amazonite', 'Sodolite', 'Rose Quartz', 'Black Aqeeq'
  ];

  const TOTAL_BEADS = 12;

  const STATUS_SEQUENCE = [
    { value: 'configured', label: 'Bracelet is Configured', nextLabel: 'Mark as Ready to Ship' },
    { value: 'ready_to_ship', label: 'Bracelet is Ready to Ship', nextLabel: 'Mark as Shipped' },
    { value: 'shipped', label: 'Bracelet is Shipped', nextLabel: 'Mark as Delivered' },
    { value: 'delivered', label: 'Bracelet is Delivered', nextLabel: null }
  ];

  const [config, setConfig] = useState(() => {
    const existingConfig = order.bracelet_config || [];
    return STONES.map(stone => {
      const existing = existingConfig.find(c => c.stone === stone);
      return { stone, count: existing ? (parseInt(existing.count) || 0) : 0 };
    });
  });

  const [braceletStatus, setBraceletStatus] = useState(order.bracelet_status || 'not_started');
  const [selectedNextStatus, setSelectedNextStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const totalBeads = config.reduce((sum, item) => sum + (parseInt(item.count) || 0), 0);
  const remainingBeads = TOTAL_BEADS - totalBeads;
  const selectedStones = config.filter(item => (parseInt(item.count) || 0) > 0);

  const canSave = isAnalysisDone && totalBeads === TOTAL_BEADS && !saving;
  const isConfigured = braceletStatus !== 'not_started';
  const canEdit = !isConfigured || editMode;
  const currentStatusIndex = STATUS_SEQUENCE.findIndex(s => s.value === braceletStatus);
  const canProgressStatus = isConfigured && currentStatusIndex < STATUS_SEQUENCE.length - 1;

  const handleCountChange = (index, value) => {
    const parsedValue = value === '' ? '' : parseInt(value);
    const newCount = parsedValue === '' ? 0 : Math.max(0, parsedValue || 0);
    const newConfig = [...config];
    newConfig[index] = { ...newConfig[index], count: parsedValue === '' ? '' : newCount };
    setConfig(newConfig);
  };

  const handleInputBlur = (index) => {
    const newConfig = [...config];
    if (newConfig[index].count === '' || newConfig[index].count === undefined) {
      newConfig[index] = { ...newConfig[index], count: 0 };
      setConfig(newConfig);
    }
  };

  const handleSave = async () => {
    if (!isAnalysisDone) {
      toast.error('Please complete Astro Analysis first before configuring bracelet');
      return;
    }
    if (totalBeads !== TOTAL_BEADS) {
      toast.error(`Please select exactly ${TOTAL_BEADS} beads. Currently: ${totalBeads}`);
      return;
    }
    setSaving(true);
    const newStatus = isConfigured ? braceletStatus : 'configured';
    const success = await onSave(order.order_id, config, newStatus);
    if (success !== false) {
      setBraceletStatus(newStatus);
      setSaved(true);
      setEditMode(false);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  const handleStatusChange = async () => {
    if (!selectedNextStatus) {
      toast.error('Please select the next status');
      return;
    }
    
    const nextStatusIndex = STATUS_SEQUENCE.findIndex(s => s.value === selectedNextStatus);
    if (nextStatusIndex !== currentStatusIndex + 1) {
      toast.error('Please follow the status sequence');
      return;
    }
    
    setSavingStatus(true);
    const success = await onSave(order.order_id, config, selectedNextStatus);
    if (success !== false) {
      setBraceletStatus(selectedNextStatus);
      setSelectedNextStatus('');
      toast.success(`Status updated to: ${STATUS_SEQUENCE[nextStatusIndex].label}`);
    }
    setSavingStatus(false);
  };

  const getStatusInfo = () => {
    switch (braceletStatus) {
      case 'not_started': return { text: 'Not Started', color: '#E65100', bg: 'rgba(255,152,0,0.15)' };
      case 'configured': return { text: 'Bracelet is Configured', color: '#1565C0', bg: 'rgba(33,150,243,0.15)' };
      case 'ready_to_ship': return { text: 'Bracelet is Ready to Ship', color: '#7B1FA2', bg: 'rgba(156,39,176,0.15)' };
      case 'shipped': return { text: 'Bracelet is Shipped', color: '#00838F', bg: 'rgba(0,150,136,0.15)' };
      case 'delivered': return { text: 'Bracelet is Delivered', color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' };
      default: return { text: 'Not Started', color: '#E65100', bg: 'rgba(255,152,0,0.15)' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d5c5', marginBottom: '20px', opacity: isAnalysisDone ? 1 : 0.7 }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', 
          background: '#f5efe6', 
          borderBottom: isExpanded ? '1px solid #e0d5c5' : 'none',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#2E221A' }}>
          💎 Bracelet Stone Configuration
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!isAnalysisDone && (
            <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(198,40,40,0.15)', color: '#C62828' }}>
              ⚠️ Complete Astro Analysis First
            </span>
          )}
          <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: statusInfo.bg, color: statusInfo.color }}>
            {statusInfo.text}
          </span>
          <span style={{ fontSize: '1.2rem', color: '#8B4513', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {config.map((item, idx) => (
            <div 
              key={item.stone} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '12px', 
                padding: '12px 16px', 
                background: (parseInt(item.count) || 0) > 0 ? 'rgba(139,69,19,0.1)' : '#faf8f5', 
                border: (parseInt(item.count) || 0) > 0 ? '2px solid #8B4513' : '1px solid #e0d5c5' 
              }}
            >
              <span style={{ fontSize: '0.9rem', fontFamily: 'Lato, sans-serif', color: '#2E221A', fontWeight: '500' }}>
                {item.stone}
              </span>
              <input
                type="number"
                min="0"
                max={TOTAL_BEADS}
                value={item.count === 0 ? '0' : item.count}
                onChange={(e) => handleCountChange(idx, e.target.value)}
                onBlur={() => handleInputBlur(idx)}
                disabled={!canEdit}
                style={{ 
                  width: '60px', padding: '8px', textAlign: 'center', 
                  border: '1px solid #e0d5c5', fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem', fontWeight: '600',
                  background: !canEdit ? '#f5f5f5' : '#fff',
                  cursor: !canEdit ? 'not-allowed' : 'text'
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '40px', marginBottom: '20px', padding: '16px', background: '#f5efe6', border: '1px solid #e0d5c5' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Total Beads</div>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.5rem', color: totalBeads === TOTAL_BEADS ? '#2E7D32' : '#E65100' }}>
              {totalBeads} / {TOTAL_BEADS}
            </div>
            {remainingBeads > 0 && <div style={{ fontSize: '0.8rem', color: '#E65100', marginTop: '4px' }}>Need {remainingBeads} more bead{remainingBeads > 1 ? 's' : ''}</div>}
            {remainingBeads < 0 && <div style={{ fontSize: '0.8rem', color: '#C62828', marginTop: '4px' }}>Remove {Math.abs(remainingBeads)} bead{Math.abs(remainingBeads) > 1 ? 's' : ''}</div>}
          </div>
          
          {selectedStones.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Selected Stones</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedStones.map(item => (
                  <span key={item.stone} style={{ padding: '4px 10px', background: '#8B4513', color: '#fff', fontSize: '0.75rem' }}>
                    {item.stone}: {item.count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {(!isConfigured || editMode) && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '12px 24px', fontFamily: 'Cinzel, serif', fontSize: '0.9rem',
                background: saved ? '#2E7D32' : !canSave ? '#ccc' : '#8B4513',
                color: '#fff', border: 'none',
                cursor: !canSave ? 'not-allowed' : 'pointer', letterSpacing: '0.05em'
              }}
            >
              {saving ? 'Saving...' : saved ? '✓ Saved' : !isAnalysisDone ? '🔒 Complete Analysis First' : editMode ? 'Update Config' : 'Save Bracelet Config'}
            </button>
            {editMode && (
              <button onClick={() => setEditMode(false)} style={{ padding: '12px 24px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: '#9E9E9E', color: '#fff', border: 'none', cursor: 'pointer' }}>
                Cancel
              </button>
            )}
          </div>
        )}

        {isConfigured && !editMode && (
          <div style={{ marginTop: '20px', padding: '20px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '4px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#2E7D32', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Current Status</div>
                <span style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: '600', background: statusInfo.bg, color: statusInfo.color, display: 'inline-block' }}>
                  {statusInfo.text}
                </span>
              </div>
              <button onClick={() => setEditMode(true)} style={{ padding: '8px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: '#1565C0', color: '#fff', border: 'none', cursor: 'pointer' }}>
                ✏️ Edit Stone Config
              </button>
            </div>
            
            {canProgressStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '0.85rem', color: '#2E7D32', fontWeight: '500' }}>Update Status:</div>
                <select
                  value={selectedNextStatus}
                  onChange={(e) => setSelectedNextStatus(e.target.value)}
                  style={{ padding: '10px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', border: '1px solid #4CAF50', borderRadius: '4px', background: '#fff', minWidth: '220px', cursor: 'pointer' }}
                >
                  <option value="">-- Select Next Status --</option>
                  {currentStatusIndex < STATUS_SEQUENCE.length - 1 && (
                    <option value={STATUS_SEQUENCE[currentStatusIndex + 1].value}>
                      {STATUS_SEQUENCE[currentStatusIndex + 1].label}
                    </option>
                  )}
                </select>
                <button
                  onClick={handleStatusChange}
                  disabled={!selectedNextStatus || savingStatus}
                  style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: selectedNextStatus && !savingStatus ? '#2E7D32' : '#ccc', color: '#fff', border: 'none', cursor: selectedNextStatus && !savingStatus ? 'pointer' : 'not-allowed', borderRadius: '4px' }}
                >
                  {savingStatus ? '⏳ Updating...' : '✓ Save Status'}
                </button>
              </div>
            )}
            
            {!canProgressStatus && braceletStatus === 'delivered' && (
              <div style={{ color: '#2E7D32', fontWeight: '500', fontSize: '0.9rem' }}>✅ Bracelet has been delivered to the customer</div>
            )}
            
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #c8e6c9' }}>
              <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', marginBottom: '12px' }}>Status Progress</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {STATUS_SEQUENCE.map((status, idx) => {
                  const isCurrent = status.value === braceletStatus;
                  const isPast = idx < currentStatusIndex;
                  return (
                    <div 
                      key={status.value}
                      style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: '600', background: isPast ? '#4CAF50' : isCurrent ? '#1565C0' : '#e0e0e0', color: (isPast || isCurrent) ? '#fff' : '#666', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {isPast && '✓'} {idx + 1}. {status.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// RECOMMEND ANUSTHAN SECTION
// ══════════════════════════════════════════

export const RecommendAnusthanSection = ({ order, anusthans, onSave, isAnalysisDone }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selected, setSelected] = useState(() => {
    if (order.recommendations && order.recommendations.length > 0) {
      return order.recommendations.map(r => r.anusthan_id);
    }
    return [];
  });
  const [isNA, setIsNA] = useState(order.recommendations_na || false);
  const [saving, setSaving] = useState(false);
  const [savedRecommendations, setSavedRecommendations] = useState(order.recommendations || []);

  const hasSavedRecommendations = savedRecommendations.length > 0 || order.recommendations_na;
  const canSave = isAnalysisDone && (isNA || selected.length > 0) && !saving;
  
  const getRecommendationStatus = () => {
    if (order.recommendations_na) {
      return { text: 'Not Recommended (N/A)', color: '#757575', bg: 'rgba(158,158,158,0.15)' };
    }
    if (savedRecommendations.length > 0 || (order.recommendations && order.recommendations.length > 0)) {
      return { text: 'Recommended', color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' };
    }
    return { text: 'Not Recommended', color: '#E65100', bg: 'rgba(255,152,0,0.15)' };
  };

  const statusInfo = getRecommendationStatus();

  const toggleAnusthan = (anusthanId) => {
    if (isNA) return;
    setSelected(prev => 
      prev.includes(anusthanId) 
        ? prev.filter(id => id !== anusthanId)
        : [...prev, anusthanId]
    );
  };

  const handleNAToggle = () => {
    setIsNA(!isNA);
    if (!isNA) setSelected([]);
  };

  const handleSave = async () => {
    if (!isAnalysisDone) {
      toast.error('Please complete Astro Analysis first before recommending anusthans');
      return;
    }
    setSaving(true);
    const recommendations = isNA 
      ? [] 
      : selected.map(id => {
          const anusthan = anusthans.find(a => a.id === id);
          return {
            anusthan_id: id,
            anusthan_name: anusthan?.name || '',
            anusthan_icon: anusthan?.icon || '🪔',
            status: 'pending',
            recommended_at: new Date().toISOString()
          };
        });
    await onSave(order.order_id, isNA ? 'NA' : recommendations);
    if (!isNA) {
      setSavedRecommendations(recommendations);
    } else {
      setSavedRecommendations([]);
    }
    setSaving(false);
  };

  const displayRecommendations = savedRecommendations.length > 0 ? savedRecommendations : (order.recommendations || []);

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d5c5', marginBottom: '20px', opacity: isAnalysisDone ? 1 : 0.7 }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', background: '#f5efe6', 
          borderBottom: isExpanded ? '1px solid #e0d5c5' : 'none',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#2E221A' }}>
          🪔 Recommend Anusthan to Customer
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!isAnalysisDone && (
            <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(198,40,40,0.15)', color: '#C62828' }}>
              ⚠️ Complete Astro Analysis First
            </span>
          )}
          <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: statusInfo.bg, color: statusInfo.color }}>
            {statusInfo.text}
          </span>
          <span style={{ fontSize: '1.2rem', color: '#8B4513', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input type="checkbox" checked={isNA} onChange={handleNAToggle} style={{ width: '18px', height: '18px' }} />
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#2E221A' }}>N/A - No Anusthan Recommended</span>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px', opacity: isNA ? 0.5 : 1 }}>
          {anusthans.map(anusthan => (
            <div
              key={anusthan.id}
              onClick={() => toggleAnusthan(anusthan.id)}
              style={{
                padding: '14px 16px',
                border: selected.includes(anusthan.id) ? '2px solid #8B4513' : '1px solid #e0d5c5',
                background: selected.includes(anusthan.id) ? 'rgba(139,69,19,0.1)' : '#fff',
                cursor: isNA ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '12px'
              }}
            >
              <input type="checkbox" checked={selected.includes(anusthan.id)} readOnly style={{ width: '18px', height: '18px' }} />
              <div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', fontWeight: '500', color: '#2E221A' }}>
                  {anusthan.icon} {anusthan.name}
                </div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', color: '#725b47' }}>{anusthan.display_date}</div>
              </div>
            </div>
          ))}
        </div>

        {selected.length > 0 && !isNA && (
          <div style={{ marginBottom: '20px', padding: '12px', background: '#f5efe6', border: '1px solid #e0d5c5' }}>
            <span style={{ fontSize: '0.8rem', color: '#725b47' }}>Selected: {selected.length} anusthan(s) will be recommended to the customer</span>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={!canSave}
          style={{
            padding: '12px 24px', fontFamily: 'Cinzel, serif', fontSize: '0.9rem',
            background: !canSave ? '#ccc' : '#8B4513', color: '#fff', border: 'none',
            cursor: !canSave ? 'not-allowed' : 'pointer', letterSpacing: '0.05em'
          }}
        >
          {saving ? 'Saving...' : !isAnalysisDone ? '🔒 Complete Analysis First' : 'Save Recommendations'}
        </button>

        {displayRecommendations.length > 0 && !isNA && (
          <div style={{ marginTop: '20px', padding: '16px', background: '#f0fff0', border: '1px solid #c8e6c9' }}>
            <div style={{ fontSize: '0.75rem', color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px', fontWeight: '600' }}>
              ✓ Recommended Anusthans (Saved)
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {displayRecommendations.map((rec, idx) => (
                <span key={idx} style={{ padding: '8px 14px', background: '#fff', border: '1px solid #c8e6c9', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#2E221A' }}>
                  {rec.anusthan_icon || '🪔'} {rec.anusthan_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
